// ModalInfo.jsx
import React, { useEffect, useState, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import PropTypes from 'prop-types';
import axios from 'axios';
import Swal from 'sweetalert2';
import SoftTypography from 'components/SoftTypography';
import InfoIcon from '@mui/icons-material/Info';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlaceIcon from '@mui/icons-material/Place';

import TimelineList from 'prefabsComponents/Timeline/TimelineList';
import TimelineItem from 'prefabsComponents/Timeline/TimelineItem';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { jwtDecode } from 'jwt-decode';

// 👇 Auth
import { AuthContext } from 'context/AuthContext';

// Función para obtener una cookie por nombre
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function ModalInfo({ show, handleClose, numeroOrdenCabecera }) {
  const [orderInfo, setOrderInfo] = useState(null);
  const [timelineData, setTimelineData] = useState([]);
  const [showNewTrackingModal, setShowNewTrackingModal] = useState(false);
  const [newTrackingText, setNewTrackingText] = useState('');

  // 👇 Solo admin/user pueden agregar seguimientos (botón + y modal)
  const { hasRole } = useContext(AuthContext);
  const canAddTracking = hasRole('admin', 'user');

  useEffect(() => {
    const fetchOrderInfo = async () => {
      if (numeroOrdenCabecera) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/api/orders/orden-info/`,
            { params: { id: numeroOrdenCabecera } }
          );
          setOrderInfo(response.data[0]);
        } catch (error) {
          console.error('Error fetching order information:', error);
        }
      }
    };

    const fetchOrdertracking = async () => {
      if (numeroOrdenCabecera) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/api/orders/tracking/${numeroOrdenCabecera}/`
          );
          setTimelineData(response.data);
        } catch (error) {
          console.error('Error fetching order tracking:', error);
        }
      }
    };

    if (show) {
      fetchOrderInfo();
      fetchOrdertracking();
    }
  }, [show, numeroOrdenCabecera]);

  // Guardar nuevo seguimiento (solo si hay permiso)
  const handleSaveTracking = async () => {
    if (!canAddTracking) return; // seguridad extra
    if (!newTrackingText.trim()) {
      alert('El seguimiento no puede estar vacío.');
      return;
    }

    // Extraer el token de la cookie y decodificar para obtener el user_id
    const token = getCookie('access_token');
    let user_id = null;
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.user_id) {
          user_id = decoded.user_id;
        }
      } catch (error) {
        console.error('Error al decodificar el token JWT:', error);
      }
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/orders/tracking/${numeroOrdenCabecera}/`,
        {
          detalle: newTrackingText,
          id_orden_cabecera: numeroOrdenCabecera,
          user_id, // Enviamos el id del usuario
        }
      );

      // Recargar la lista de seguimientos
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/orders/tracking/${numeroOrdenCabecera}/`
      );
      setTimelineData(response.data);

      // Cerrar modal y limpiar campo
      setShowNewTrackingModal(false);
      setNewTrackingText('');
    } catch (error) {
      console.error('Error al guardar el seguimiento:', error);
    }
  };

  // Eliminar seguimiento (el botón eliminar ya está protegido en TimelineItem por rol)
  const handleDeleteTracking = (id_seguimiento_orden) => {
    Swal.fire({
      title: '¿Está seguro?',
      text: 'Esta acción eliminará el seguimiento.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `${process.env.REACT_APP_SERVER_URL}/api/orders/tracking/${numeroOrdenCabecera}/`,
            { data: { id_seguimiento_orden } }
          );
          setTimelineData((prevData) =>
            prevData.filter((item) => item.id_seguimiento_orden !== id_seguimiento_orden)
          );
          Swal.fire('Eliminado!', 'El seguimiento ha sido eliminado.', 'success');
        } catch (error) {
          console.error('Error al eliminar el seguimiento:', error);
          Swal.fire('Error', 'No se pudo eliminar el seguimiento.', 'error');
        }
      }
    });
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
        backdropClassName="modal-backdrop"
        className="modal-container"
        style={{ zIndex: 1050 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Detalles de la Orden</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Información de la orden */}
          <div className="order-info" style={{ marginBottom: '20px' }}>
            <SoftTypography variant="h5" fontWeight="bold" gutterBottom>
              <InfoIcon /> Información de la orden
            </SoftTypography>

            {orderInfo ? (
              <>
                <SoftTypography variant="body2" fontWeight="medium">
                  <strong>#{orderInfo.numero}</strong>
                </SoftTypography>
                <SoftTypography variant="body2">
                  <LocalShippingIcon /> {orderInfo.cliente}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <LocalShippingIcon /> {orderInfo.proveedor}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <PersonIcon /> {orderInfo.responsable}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <PublicIcon /> {orderInfo.incoterms}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <LocalShippingIcon /> {orderInfo.transporte}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <PlaceIcon /> {orderInfo.origen}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <CalendarTodayIcon /> {orderInfo.estado}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <CalendarTodayIcon /> {orderInfo.fecha}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <CalendarTodayIcon /> {orderInfo.entrega}
                </SoftTypography>
                <SoftTypography variant="body2">
                  <CalendarTodayIcon /> {orderInfo.real_entrega}
                </SoftTypography>
              </>
            ) : (
              <SoftTypography variant="body2" color="textSecondary">
                Cargando información de la orden...
              </SoftTypography>
            )}
          </div>

          {/* Seguimientos dinámicos */}
          <SoftBox className="order-tracking">
            <SoftTypography variant="h5" fontWeight="bold" gutterBottom>
              <InfoIcon /> Seguimientos
            </SoftTypography>
            <TimelineList title="" white>
              {timelineData.length > 0 ? (
                [...timelineData].reverse().map((item, index, array) => {
                  const isLast = index === array.length - 1;
                  const color = isLast ? 'success' : 'info';
                  const icon = isLast ? 'place_icon' : 'check_circle';
                  return (
                    <TimelineItem
                      key={item.id_seguimiento_orden}
                      color={color}
                      icon={icon}
                      title={item.usuario}
                      dateTime={item.fecha}
                      description={<div style={{ whiteSpace: 'pre-line' }}>{item.detalle}</div>}
                      lastItem={isLast}
                      onDelete={handleDeleteTracking}
                      trackingId={item.id_seguimiento_orden}
                    />
                  );
                })
              ) : (
                <SoftTypography variant="body2" color="textSecondary">
                  No hay seguimientos registrados.
                </SoftTypography>
              )}

              {/* ➕ Botón agregar seguimiento: SOLO admin/user */}
              {canAddTracking && (
                <SoftBox display="flex" justifyContent="center" mt={2}>
                  <SoftButton
                    variant="gradient"
                    color="info"
                    onClick={() => setShowNewTrackingModal(true)}
                  >
                    +
                  </SoftButton>
                </SoftBox>
              )}
            </TimelineList>
          </SoftBox>
        </Modal.Body>
        <Modal.Footer>
          <SoftButton variant="gradient" color="error" onClick={handleClose}>
            Cerrar
          </SoftButton>
        </Modal.Footer>
      </Modal>

      {/* Modal para agregar un nuevo seguimiento — SOLO admin/user */}
      {canAddTracking && (
        <Modal
          show={showNewTrackingModal}
          onHide={() => setShowNewTrackingModal(false)}
          centered
          backdrop="static"
          backdropClassName="modal-backdrop"
          style={{ zIndex: 1050 }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Agregar Seguimiento</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <TextareaAutosize
              placeholder="Ingrese el detalle del seguimiento..."
              style={{ width: '100%', padding: '10px', borderRadius: '5px', fontSize: '1.5vh' }}
              value={newTrackingText}
              onChange={(e) => setNewTrackingText(e.target.value)}
              rows={5}
            />
          </Modal.Body>
          <Modal.Footer>
            <SoftButton variant="gradient" color="success" onClick={handleSaveTracking}>
              Guardar
            </SoftButton>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

ModalInfo.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  numeroOrdenCabecera: PropTypes.string,
};

export default ModalInfo;
